/**
 * The Mail search box: Gmail's operators plus what `parseMailSearch` doesn't read. A query is plain text, so nothing
 * typed is ever lost; the box shows its filters as chips (`mailQueryChips`) and writes them back (`formatMailQuery`).
 *
 *   from:acme.com OR to:=bruno@globex.com in:propostas -in:processos nota fiscal
 *
 * - A filter's value matches like (`from:acme`, contains), exactly (`from:=ana@acme.com`) or is left out (`-from:acme`).
 *   Dates, `has:` and `is:` take no match mode.
 * - `in:` takes a system folder or view, or a label's name (imported folders are labels; Gmail's `in:<label>`).
 * - `domain:` matches anyone on a domain, sender or recipient.
 * - `mailbox:` keeps one of my mailboxes (`mailbox:financeiro@`). It only ever narrows: the server looks through the
 *   mailboxes I'm a member of, so naming another one finds nothing. A group is `to:contato@`.
 * - `OR` joins the filters beside it; repeating `from:`, `to:`, `with:`, `domain:`, `in:` or `label:` means any of them
 *   (Slack; logic/searchQuery). Everything else must all match.
 * Unknown or malformed filters stay words.
 */
import type { MailLabel, MailThread, Mailbox } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { fold, matchScore } from "./search.ts";
import { searchToken, type SearchSuggestion, type SearchSuggestMenu, type SearchTextEdit } from "./searchSuggest.ts";

export type MailQueryKey = "from" | "to" | "with" | "domain" | "subject" | "in" | "label" | "mailbox" | "has" | "is" | "after" | "before";

export const MAIL_QUERY_KEYS: MailQueryKey[] = ["from", "to", "with", "domain", "subject", "in", "label", "mailbox", "has", "is", "after", "before"];

/** The filters that take a match mode (`like`, `=`, `≠`). */
export const MAIL_MATCH_KEYS: MailQueryKey[] = ["from", "to", "with", "domain", "subject", "in", "label", "mailbox"];

/** Repeating one of these means "any of them". */
const ANY_KEYS: MailQueryKey[] = ["from", "to", "with", "domain", "in", "label", "mailbox"];

/** System folders and views `in:` knows; any other value is a label's name. */
export const MAIL_SYSTEM_IN = ["inbox", "sent", "archive", "trash", "spam", "starred", "snoozed", "scheduled", "anywhere"];

/** One condition. `key` "text" is plain words. `value` is lower case, except a date (start of that day, UTC, as text). */
export type MailQueryTerm = { key: MailQueryKey | "text"; value: string; exact?: true; not?: true };

/** All groups must match; within a group, any term. */
export type MailQuery = { groups: MailQueryTerm[][] };

/** A chip in the box. `or`: joined to the chip before it by a typed `OR`. `value` keeps the case it was typed in. */
export type MailChip = { key: MailQueryKey; value: string; exact?: true; not?: true; or?: true };

export type MailChipMode = "like" | "exact" | "exclude";

type Token =
  | { kind: "or" }
  | { kind: "word"; raw: string; text: string; not: boolean }
  | { kind: "filter"; raw: string; key: MailQueryKey; value: string; exact: boolean; not: boolean };

function searchDate(v: string): number | undefined {
  const m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(v);
  if (m === null) return undefined;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(t) ? undefined : t;
}

function validFilter(key: MailQueryKey, value: string): boolean {
  if (value === "") return false;
  const lower = value.toLowerCase();
  if (key === "has") return lower === "attachment";
  if (key === "is") return lower === "unread" || lower === "read" || lower === "starred";
  if (key === "after" || key === "before") return searchDate(value) !== undefined;
  return true;
}

function tokens(q: string): Token[] {
  const out: Token[] = [];
  for (const m of q.matchAll(/(-?)(\w+):(=?)"([^"]*)"|(-?)(\w+):(=?)(\S+)|(-?)"([^"]*)"|(\S+)/g)) {
    const raw = m[0];
    const rawKey = m[2] ?? m[6];
    if (rawKey !== undefined) {
      const key = rawKey.toLowerCase() as MailQueryKey;
      const value = (m[4] ?? m[8] ?? "").trim();
      const exact = (m[3] ?? m[7]) === "=";
      const not = (m[1] ?? m[5]) === "-";
      if (MAIL_QUERY_KEYS.includes(key) && validFilter(key, value) && (MAIL_MATCH_KEYS.includes(key) || (!exact && !not))) {
        out.push({ kind: "filter", raw, key, value, exact, not });
      } else out.push({ kind: "word", raw, text: raw, not: false });
      continue;
    }
    if (raw === "OR") {
      out.push({ kind: "or" });
      continue;
    }
    if (m[10] !== undefined) {
      const text = m[10].trim();
      if (text !== "") out.push({ kind: "word", raw, text, not: m[9] === "-" });
      continue;
    }
    const not = raw.length > 1 && raw.startsWith("-");
    out.push({ kind: "word", raw, text: not ? raw.slice(1) : raw, not });
  }
  return out;
}

function term(t: Exclude<Token, { kind: "or" }>): MailQueryTerm {
  const out: MailQueryTerm =
    t.kind === "word"
      ? { key: "text", value: t.text.toLowerCase() }
      : { key: t.key, value: t.key === "after" || t.key === "before" ? String(searchDate(t.value)) : t.value.toLowerCase() };
  if (t.kind === "filter" && t.exact) out.exact = true;
  if (t.not) out.not = true;
  return out;
}

/** Reads a search box's text into its groups. */
export function parseMailQuery(q: string): MailQuery {
  const groups: MailQueryTerm[][] = [];
  let joinNext = false;
  for (const t of tokens(q)) {
    if (t.kind === "or") {
      joinNext = groups.length > 0;
      continue;
    }
    const next = term(t);
    if (joinNext) {
      groups[groups.length - 1]!.push(next);
      joinNext = false;
      continue;
    }
    const repeat = next.not === true || !ANY_KEYS.includes(next.key as MailQueryKey) ? undefined : groups.find((g) => g.every((x) => x.key === next.key && x.not !== true));
    if (repeat !== undefined) repeat.push(next);
    else groups.push([next]);
  }
  return { groups };
}

/** The value as a query writes it (quoted when it needs to be). */
function written(value: string): string {
  return /[\s"]/u.test(value) ? `"${value.replace(/"/g, "")}"` : value;
}

function chipText(c: MailChip): string {
  return `${c.not === true ? "-" : ""}${c.key}:${c.exact === true ? "=" : ""}${written(c.value)}`;
}

/** The query for these chips and the words typed after them. */
/** One term on a conversation in hand; `undefined`: it needs what only the server has. */
function localTerm(term: MailQueryTerm, labels: MailLabel[], mailboxes: Pick<Mailbox, "id" | "address">[]): ((t: MailThread) => boolean) | undefined {
  const { key, value: v, exact = false, not = false } = term;
  const like = (s: string) => (exact ? s.toLowerCase() === v : s.toLowerCase().includes(v));
  const yes = (test: (t: MailThread) => boolean) => (not ? (t: MailThread) => !test(t) : test);
  const labelled = () => {
    const ids = labels.filter((l) => like(l.name)).map((l) => l.id);
    return yes((t) => (t.labelIds ?? []).some((id) => ids.includes(id)));
  };
  switch (key) {
    case "text": {
      if (not) return undefined;
      const word = fold(v);
      return (t) => fold([t.subject, t.snippet, ...t.participants.flatMap((p) => [p.name ?? "", p.address])].join(" ")).split(/[^\p{L}\p{N}]+/u).some((w) => w.startsWith(word));
    }
    // Who's on the conversation, not on which message: the people terms only narrow, and the server has the last word.
    case "from":
    case "to":
    case "with":
      return not ? undefined : (t) => t.participants.some((p) => like(p.address) || (key !== "with" && like(p.name ?? "")));
    case "domain":
      return not ? undefined : (t) => t.participants.some((p) => like(p.address.slice(p.address.lastIndexOf("@") + 1)));
    case "subject":
      return yes((t) => like(t.subject));
    case "has":
      return (t) => t.hasAttachments;
    case "is":
      return (t) => (v === "starred" ? t.starred : v === "unread" ? t.unread : !t.unread);
    case "after":
      return (t) => t.lastAt >= Number(v);
    case "before":
      return (t) => t.lastAt < Number(v);
    case "label":
      return labelled();
    case "mailbox": {
      const ids = mailboxes.filter((m) => like(m.address)).map((m) => m.id);
      return yes((t) => ids.includes(t.mailboxId));
    }
    case "in":
      if (v === "anywhere") return yes(() => true);
      if (v === "starred") return yes((t) => t.starred);
      if (v === "snoozed") return yes((t) => t.snoozedUntil !== undefined);
      if (v === "scheduled") return yes((t) => t.scheduledAt !== undefined);
      return MAIL_SYSTEM_IN.includes(v) ? yes((t) => t.folder === v) : labelled();
  }
}

/**
 * The search on conversations already on the device, for the first keystroke; the server's answer replaces it. Like the
 * server, Spam and Trash stay out unless the search is in them (`folder`, the one open) or asks for them.
 * `undefined`: a term only the server can judge (a word left out, or a person left out).
 */
export function mailQueryLocal(query: MailQuery, labels: MailLabel[], folder: string, mailboxes: Pick<Mailbox, "id" | "address">[] = []): ((t: MailThread) => boolean) | undefined {
  const groups: ((t: MailThread) => boolean)[][] = [];
  for (const g of query.groups) {
    const tests = g.map((term) => localTerm(term, labels, mailboxes));
    if (tests.includes(undefined)) return undefined;
    groups.push(tests as ((t: MailThread) => boolean)[]);
  }
  const inFolder = query.groups.some((g) => g.some((term) => term.key === "in" && term.not !== true && ["spam", "trash", "anywhere"].includes(term.value)));
  const hidden = (t: MailThread) => !inFolder && (folder === "spam" || folder === "trash" ? t.folder !== folder : t.folder === "spam" || t.folder === "trash");
  return (t) => !hidden(t) && groups.every((g) => g.some((test) => test(t)));
}

export function formatMailQuery(chips: MailChip[], draft: string): string {
  const parts = chips.map((c, i) => (c.or === true && i > 0 ? `OR ${chipText(c)}` : chipText(c)));
  if (draft !== "") parts.push(draft);
  return parts.join(" ");
}

/** A query's filters as chips, and the words left over: what the box shows for a query it didn't build (a link). */
export function mailQueryChips(q: string): { chips: MailChip[]; draft: string } {
  const chips: MailChip[] = [];
  const words: string[] = [];
  let joinNext = false;
  for (const t of tokens(q)) {
    if (t.kind === "or") {
      if (chips.length > 0) joinNext = true;
      else words.push("OR");
      continue;
    }
    if (t.kind === "word") {
      if (joinNext) words.push("OR");
      joinNext = false;
      words.push(t.raw);
      continue;
    }
    const chip: MailChip = { key: t.key, value: t.value };
    if (t.exact) chip.exact = true;
    if (t.not) chip.not = true;
    if (joinNext) chip.or = true;
    joinNext = false;
    chips.push(chip);
  }
  return { chips, draft: words.join(" ") };
}

/**
 * The field's text after a keystroke: a complete filter followed by a space becomes a chip, and `OR ` joins the next
 * chip to the last one. Undefined when nothing commits. `or` says this filter was started with `OR`.
 */
export function commitMailChip(draft: string, chips: MailChip[]): { chips: MailChip[]; draft: string } | undefined {
  const joined = draft.startsWith("OR ") && chips.length > 0;
  const text = joined ? draft.slice(3) : draft;
  if (!/\s$/u.test(text)) return undefined;
  const open = (text.match(/"/g) ?? []).length % 2 === 1;
  if (open) return undefined;
  const parsed = mailQueryChips(text.trim());
  const one = parsed.chips.length === 1 ? parsed.chips[0] : undefined;
  if (one === undefined) return undefined;
  return { chips: [...chips, joined ? { ...one, or: true } : one], draft: parsed.draft === "" ? "" : `${parsed.draft} ` };
}

/** Whether each chip reads as "any of" with the one before it: the box draws "or" between them. */
export function mailChipOrs(chips: MailChip[]): boolean[] {
  return chips.map((c, i) => {
    if (i === 0) return false;
    if (c.or === true) return true;
    const p = chips[i - 1]!;
    return c.key === p.key && ANY_KEYS.includes(c.key) && c.not !== true && p.not !== true;
  });
}

export function mailChipMode(c: MailChip): MailChipMode {
  return c.not === true ? "exclude" : c.exact === true ? "exact" : "like";
}

export function setMailChipMode(c: MailChip, mode: MailChipMode): MailChip {
  const { exact: _exact, not: _not, ...rest } = c;
  if (mode === "exact") return { ...rest, exact: true };
  if (mode === "exclude") return { ...rest, not: true };
  return rest;
}

// ---------------------------------------------------------------------------------------------------------------
// Suggestions (the same menu as the Search screen's, logic/searchSuggest)

/** What the menu draws on: my labels, and people I've mailed with (from the open list and contacts). */
export type MailSuggestContext = {
  labels: { name: string }[];
  /** My mailboxes, for `mailbox:`. */
  mailboxes?: { address: string }[];
  people: { name?: string | undefined; address: string }[];
  today: string;
};

const MAIL_SUGGEST_MAX = 9;

const FILTERS: [MailQueryKey | "OR", MessageDescriptor][] = [
  ["from", msg("mail.search.filter.from")],
  ["to", msg("mail.search.filter.to")],
  ["domain", msg("mail.search.filter.domain")],
  ["mailbox", msg("mail.search.filter.mailbox")],
  ["in", msg("mail.search.filter.in")],
  ["has", msg("mail.search.filter.has")],
  ["is", msg("mail.search.filter.is")],
  ["before", msg("mail.search.filter.before")],
  ["after", msg("mail.search.filter.after")],
  ["OR", msg("mail.search.filter.or")],
];

function valueRow(key: MailQueryKey, val: string, label: string, description?: MessageDescriptor): SearchSuggestion {
  const item: SearchSuggestion = { kind: "value", key, value: val, label, insert: `${key}:${written(val)}` };
  if (description !== undefined) item.description = description;
  return item;
}

/** Whether a value belongs in the menu: all of them until a letter is typed. */
function fits(query: string, score: number): boolean {
  return query === "" || score >= 40;
}

function domainOf(address: string): string | undefined {
  const at = address.lastIndexOf("@");
  return at < 0 ? undefined : address.slice(at + 1).toLowerCase();
}

function domains(key: MailQueryKey, query: string, ctx: MailSuggestContext): SearchSuggestion[] {
  const seen = new Set<string>();
  for (const p of ctx.people) {
    const d = domainOf(p.address);
    if (d !== undefined && d !== "" && fits(query, matchScore(query, d))) seen.add(d);
  }
  return [...seen].toSorted((a, b) => matchScore(query, b) - matchScore(query, a) || (a < b ? -1 : 1)).map((d) => valueRow(key, d, d, msg("mail.search.value.domain")));
}

function people(key: MailQueryKey, query: string, ctx: MailSuggestContext): SearchSuggestion[] {
  const ranked: { item: SearchSuggestion; score: number; sort: string }[] = [];
  const seen = new Set<string>();
  for (const p of ctx.people) {
    const address = p.address.toLowerCase();
    if (seen.has(address)) continue;
    seen.add(address);
    const score = Math.max(matchScore(query, p.name ?? ""), matchScore(query, address));
    if (!fits(query, score)) continue;
    const item: SearchSuggestion = { kind: "person", key, id: address, label: p.name !== undefined && p.name !== "" ? p.name : address, insert: `${key}:${address}` };
    if (p.name !== undefined && p.name !== "") item.detail = address;
    ranked.push({ item, score, sort: fold(p.name ?? address) });
  }
  ranked.sort((a, b) => b.score - a.score || (a.sort < b.sort ? -1 : a.sort > b.sort ? 1 : 0));
  return ranked.map((r) => r.item);
}

function folders(query: string, ctx: MailSuggestContext): SearchSuggestion[] {
  const q = query.toLowerCase();
  const system = MAIL_SYSTEM_IN.filter((f) => f.startsWith(q)).map((f) => valueRow("in", f, f, msg("mail.search.value.folder")));
  const labels = ctx.labels
    .map((l) => ({ name: l.name, score: matchScore(query, l.name) }))
    .filter((l) => fits(query, l.score))
    .toSorted((a, b) => b.score - a.score || fold(a.name).localeCompare(fold(b.name)))
    .map((l) => valueRow("in", l.name, l.name, msg("mail.search.value.label")));
  return [...labels, ...system];
}

function enumValues(key: "has" | "is", query: string): SearchSuggestion[] {
  const list: [string, MessageDescriptor][] =
    key === "has"
      ? [["attachment", msg("mail.search.value.attachment")]]
      : [
          ["unread", msg("mail.search.value.unread")],
          ["read", msg("mail.search.value.read")],
          ["starred", msg("mail.search.value.starred")],
        ];
  return list.filter(([v]) => v.startsWith(query.toLowerCase())).map(([v, d]) => valueRow(key, v, `${key}:${v}`, d));
}

function dates(key: "before" | "after", query: string, today: string): SearchSuggestion[] {
  const y = new Date(Date.parse(today) - 86_400_000).toISOString().slice(0, 10);
  const list: [string, MessageDescriptor][] = [
    [today, msg("search.value.today")],
    [y, msg("search.value.yesterday")],
  ];
  return list.filter(([v]) => v.startsWith(query)).map(([v, d]) => valueRow(key, v, `${key}:${v}`, d));
}

/** The menu for the token at `caret`, or undefined when there's nothing to offer. */
export function mailSearchSuggestions(text: string, caret: number, ctx: MailSuggestContext): SearchSuggestMenu | undefined {
  const { start, end, typed: token } = searchToken(text, caret);
  if (token.includes('"')) return undefined;
  const exclude = token.startsWith("-");
  const typed = exclude ? token.slice(1) : token;
  const colon = typed.indexOf(":");
  let items: SearchSuggestion[];
  let key: MailQueryKey | undefined;
  let query: string;
  if (colon < 0) {
    query = typed;
    const q = typed.toLowerCase();
    if (q === "" && text.trim() !== "") return undefined;
    items = FILTERS.filter(([k]) => k.toLowerCase().startsWith(q) && (!exclude || k !== "OR")).map(([k, description]) =>
      k === "OR" ? { kind: "filter", key: "or", label: "OR", description, insert: "OR " } : { kind: "filter", key: k, label: `${k}:`, description, insert: `${k}:` },
    );
  } else {
    const k = typed.slice(0, colon).toLowerCase() as MailQueryKey;
    if (!MAIL_QUERY_KEYS.includes(k)) return undefined;
    key = k;
    query = typed.slice(colon + 1).replace(/^=/, "");
    const prefix = typed.slice(colon + 1).startsWith("=") ? "=" : "";
    switch (k) {
      case "from":
      case "to":
      case "with":
        items = [...domains(k, query, ctx), ...people(k, query, ctx)];
        break;
      case "domain":
        items = domains(k, query, ctx);
        break;
      case "in":
      case "label":
        items = k === "in" ? folders(query, ctx) : folders(query, ctx).filter((i) => i.kind === "value" && !MAIL_SYSTEM_IN.includes(i.value)).map((i) => ({ ...i, key: k, insert: i.insert.replace(/^in:/, "label:") }));
        break;
      case "mailbox":
        items = (ctx.mailboxes ?? []).filter((m) => fits(query, matchScore(query, m.address))).map((m) => valueRow("mailbox", m.address, m.address, msg("mail.search.value.mailbox")));
        break;
      case "has":
      case "is":
        items = enumValues(k, query);
        break;
      case "after":
      case "before":
        items = dates(k, query, ctx.today);
        break;
      default:
        return undefined;
    }
    if (prefix !== "") items = items.map((i) => ({ ...i, insert: i.insert.replace(":", ":=") }));
  }
  if (items.length === 0) return undefined;
  if (exclude) items = items.map((i) => ({ ...i, label: i.kind === "filter" ? `-${i.label}` : i.label, insert: `-${i.insert}` }));
  const menu: SearchSuggestMenu = { start, end, query, items: items.slice(0, MAIL_SUGGEST_MAX) };
  if (key !== undefined) menu.key = key;
  if (exclude) menu.exclude = true;
  return menu;
}

export type { SearchTextEdit };

// ---------------------------------------------------------------------------------------------------------------
// The filter row: the same thing as a typed query, so a chip and `is:unread` can't disagree.

/** What the filter row holds. `mailbox` and `group` are addresses, a date is `YYYY-MM-DD`, `label` is its name. */
export type MailFilters = {
  mailbox?: string;
  group?: string;
  unread?: true;
  starred?: true;
  attachment?: true;
  from?: string;
  after?: string;
  before?: string;
  label?: string;
};

/** The filter row as a query (the list's `filter`). */
export function mailFilterQuery(f: MailFilters): string {
  const chips: MailChip[] = [];
  if (f.mailbox !== undefined) chips.push({ key: "mailbox", value: f.mailbox, exact: true });
  if (f.group !== undefined) chips.push({ key: "to", value: f.group, exact: true });
  if (f.unread === true) chips.push({ key: "is", value: "unread" });
  if (f.starred === true) chips.push({ key: "is", value: "starred" });
  if (f.attachment === true) chips.push({ key: "has", value: "attachment" });
  if (f.from !== undefined) chips.push({ key: "from", value: f.from });
  if (f.after !== undefined) chips.push({ key: "after", value: f.after });
  if (f.before !== undefined) chips.push({ key: "before", value: f.before });
  if (f.label !== undefined) chips.push({ key: "label", value: f.label, exact: true });
  return formatMailQuery(chips, "");
}

/** The list's own filters (Unread through Label, not the mailbox) as the route keeps them: `?filter=` (logic/shell). */
export function mailListFilterQuery(f: MailFilters): string {
  return mailFilterQuery(listFiltersOf(f));
}

/** Unread through Label: the filters that belong to the list in view, without the mailbox. */
export function listFiltersOf(f: MailFilters): MailFilters {
  const { mailbox: _m, group: _g, ...listed } = f;
  void _m;
  void _g;
  return listed;
}

/** A route's `?filter=` read back into the filter row; anything it doesn't hold is dropped. */
export function mailListFilters(q: string): MailFilters {
  const f: MailFilters = {};
  for (const c of mailQueryChips(q).chips) {
    if (c.not === true) continue;
    if (c.key === "is" && c.value === "unread") f.unread = true;
    else if (c.key === "is" && c.value === "starred") f.starred = true;
    else if (c.key === "has" && c.value === "attachment") f.attachment = true;
    else if (c.key === "from") f.from = c.value;
    else if (c.key === "after") f.after = c.value;
    else if (c.key === "before") f.before = c.value;
    else if (c.key === "label") f.label = c.value;
  }
  return f;
}

/** Whether anything but the mailbox is on (the mailbox persists; the rest belongs to the list in view). */
export function hasListFilters(f: MailFilters): boolean {
  return f.unread === true || f.starred === true || f.attachment === true || f.from !== undefined || f.after !== undefined || f.before !== undefined || f.label !== undefined;
}

/** A saved search, read back for the Recent list: its filters as chips, then its words. */
export function mailRecentChips(query: string): { chips: string[]; text: string } {
  const { chips, draft } = mailQueryChips(query);
  return { chips: chips.map((c) => `${c.not === true ? "-" : ""}${c.key}: ${c.value}`), text: draft };
}

/** How many saved searches the box lists at once. */
export const MAIL_RECENT_MAX = 8;

/** Saved searches that fit what's typed, newest first; an empty box lists the latest. What's typed isn't offered back. */
export function matchingMailSearches<T extends { query: string }>(entries: T[], typed: string): T[] {
  const q = fold(typed.trim());
  if (q === "") return entries.slice(0, MAIL_RECENT_MAX);
  return entries.filter((e) => fold(e.query).includes(q) && fold(e.query) !== q).slice(0, MAIL_RECENT_MAX);
}
